import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinux, faWindows } from '@fortawesome/free-brands-svg-icons';
import { faDesktop, faServer } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'primereact/tooltip';

import './ServiceConfigPage.css';

// Helper: Get OS status icon and tooltip based on OS version.
function getOSIconAndTooltip(os) {
  if (/windows server 2012( r2)?/i.test(os)) {
    return { icon: 'pi pi-times-circle', tooltip: 'Windows Server 2012/R2 reached end of extended support on October 10, 2023.' };
  } else if (/windows server 2008( r2)/i.test(os)) {
    return { icon: 'pi pi-times-circle', tooltip: 'Windows Server 2008/R2 reached end of support on January 14, 2020.' };
  } else if (/windows server 2003/i.test(os)) {
    return { icon: 'pi pi-times-circle', tooltip: 'Windows Server 2003 reached end of support on July 14, 2015.' };
  } else if (/windows server 2000/i.test(os)) {
    return { icon: 'pi pi-times-circle', tooltip: 'Windows Server 2000 reached end of support on June 30, 2005.' };
  } else if (/windows server 2016/i.test(os)) {
    return { icon: 'pi pi-exclamation-triangle', tooltip: 'Windows Server 2016 extended support ends on January 11, 2027.' };
  }
  return null;
}

function ServiceConfigPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const { customers, setCustomers } = useContext(AppContext);

  // Define common operating systems list
  const commonOperatingSystems = [
    "Windows Server 2025",
    "Windows Server 2022",
    "Windows Server 2019",
    "Windows Server 2016",
    "Ubuntu 24.04",
    "Ubuntu 22.04",
    "Ubuntu 20.04",
    "Ubuntu 18.04",
    "Red Hat Enterprise Linux",
    "CentOS 7",
    "CentOS 8",
    "Red Hat Enterprise Linux 9",
    "Red Hat Enterprise Linux 8",
    "Red Hat Enterprise Linux 7",
    "Red Hat Enterprise Linux 6",
    "SUSE Linux Enterprise Server",
    "Other OS",
    "Windows Server 2012 R2",
    "Windows Server 2012",
    "Windows Server 2008 R2",
    "Windows Server 2008",
  ];

  // Find current customer
  const currentCustomer = customers.find(c => c.id === customerId) || {};

  // Global configuration states
  const [sslVpnUsers, setSslVpnUsers] = useState(0);
  const [rdsCalLicenses, setRdsCalLicenses] = useState(0);
  const [rdsCalType, setRdsCalType] = useState('per-user');
  const [preStagedFirewall, setPreStagedFirewall] = useState(false);
  const [preStagedDC, setPreStagedDC] = useState(false);

  // Sites state: each site has an id, name, and servers array.
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  // Track which site is being edited (for site name)
  const [editingSiteId, setEditingSiteId] = useState(null);
  // Track which site we’re uploading a file for
  const [siteToUpload, setSiteToUpload] = useState(null);
  // New state for filtering by Powerstate
  const [powerstateFilter, setPowerstateFilter] = useState("poweredOn");

  // Hidden file input for site upload (declared once)
  const fileInputRef = useRef(null);

  // On mount, load serviceData from customer (once)
  useEffect(() => {
    const cData = currentCustomer.serviceData || {};
    setSslVpnUsers(cData.sslVpnUsers || 0);
    setRdsCalLicenses(cData.rdsCalLicenses || 0);
    setRdsCalType(cData.rdsCalType || 'per-user');
    if (cData.sites && cData.sites.length > 0) {
      setSites(cData.sites);
      setSelectedSiteId(cData.sites[0].id);
    } else {
      const defaultSite = { id: Date.now().toString(), name: "Default Site", servers: [] };
      setSites([defaultSite]);
      setSelectedSiteId(defaultSite.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save sites and global config to customer's serviceData
  const saveServiceData = () => {
    setCustomers(prev => {
      return prev.map(c => {
        if (c.id !== customerId) return c;
        return {
          ...c,
          serviceData: {
            sites,
            sslVpnUsers,
            rdsCalLicenses,
            rdsCalType
          }
        };
      });
    });
  };

  // Derived: Get selected site object and its servers
  const selectedSite = sites.find(site => site.id === selectedSiteId) || { servers: [] };
  const servers = selectedSite.servers || [];

  // Filter servers by Powerstate based on powerstateFilter state.
  const filteredServers = servers.filter(s => {
    if (powerstateFilter === "All") return true;
    return s.Powerstate === powerstateFilter;
  });

  // Update the servers for the selected site
  const updateSelectedSiteServers = (newServers) => {
    const updatedSites = sites.map(site =>
      site.id === selectedSiteId ? { ...site, servers: newServers } : site
    );
    setSites(updatedSites);
    saveServiceData();
  };

  // --- Site Management ---
  const deleteSite = (siteId) => {
    const updatedSites = sites.filter(site => site.id !== siteId);
    let newSelectedSiteId = selectedSiteId;
    if (siteId === selectedSiteId) {
      newSelectedSiteId = updatedSites.length > 0 ? updatedSites[0].id : null;
    }
    setSites(updatedSites);
    setSelectedSiteId(newSelectedSiteId);
    saveServiceData();
  };

  const addSite = () => {
    const newSite = { id: Date.now().toString(), name: `Site ${sites.length + 1}`, servers: [] };
    const updatedSites = [...sites, newSite];
    setSites(updatedSites);
    setSelectedSiteId(newSite.id);
    saveServiceData();
  };

  // Function to add a pre-staged machine if not already added.
  const addPreStagedMachine = (machineName) => {
    // Check if a machine with the given name already exists (and is manually created)
    const exists = servers.some(s => s.VM === machineName && s.manuallyCreated);
    if (!exists) {
      const newMachine = {
        _rowIndex: Date.now(),
        VM: machineName,
        os: '',
        CPUs: 0,
        Memory: 0,
        "In Use MiB": 0,
        Powerstate: "poweredOn",
        services: { BaaS: false, DRaaS: false, IaaS: false },
        microsoftLicense: false,
        sqlLicenses: false,
        publicIp: false,
        manuallyCreated: true
      };
      updateSelectedSiteServers([...servers, newMachine]);
    }
  };

  // --- File Upload Handler for Site ---
  const handleSiteFileInput = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const buffer = new Uint8Array(ev.target.result);
      const workbook = XLSX.read(buffer, { type: 'array' });
      const worksheet = workbook.Sheets['vInfo'];
      if (!worksheet) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Worksheet "vInfo" not found.' });
        return;
      }
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      if (!jsonData || jsonData.length < 2) {
        toast.current?.show({ severity: 'warn', summary: 'No Data', detail: 'Spreadsheet seems empty.' });
        return;
      }
      const headers = jsonData[0];
      const rowsData = jsonData.slice(1).map((row, idx) => {
        const server = {};
        headers.forEach((header, i) => {
          server[header] = row[i];
        });
        server.services = { BaaS: false, DRaaS: false, IaaS: false };
        server.os = server['OS according to the configuration file'] || '';
        server.Memory = server.Memory ? Math.round(server.Memory / 1024) : 0;
        server["In Use MiB"] = server["In Use MiB"] ? Math.round(server["In Use MiB"] / 1000) : 0;
        server.microsoftLicense = /windows/i.test(server.os);
        server.sqlLicenses = false;
        server.publicIp = false;
        server._rowIndex = idx;
        return server;
      });
      const updatedSites = sites.map(site =>
        site.id === siteToUpload ? { ...site, servers: rowsData } : site
      );
      setSites(updatedSites);
      saveServiceData();
      toast.current?.show({ severity: 'success', summary: 'File Parsed', detail: 'Servers loaded for site' });
      e.target.value = "";
      setSiteToUpload(null);
    };
    reader.readAsArrayBuffer(file);
  };

  // --- Server Table Editing ---
  const onCellEditComplete = (e) => {
    const { rowData, newValue, field } = e;
    const value = (field === "Memory" || field === "In Use MiB")
      ? Math.round(newValue)
      : newValue;
    const updatedServers = servers.map(s =>
      s._rowIndex === rowData._rowIndex ? { ...s, [field]: value } : s
    );
    updateSelectedSiteServers(updatedServers);
  };

  const serviceCheckboxTemplate = (rowData, service) => (
    <Checkbox
      checked={rowData.services[service]}
      onChange={(e) => {
        const updatedServers = servers.map(s =>
          s._rowIndex === rowData._rowIndex ? { ...s, services: { ...s.services, [service]: e.checked } } : s
        );
        updateSelectedSiteServers(updatedServers);
      }}
    />
  );

  const selectAllService = (service) => {
    const allSelected = servers.every(s => s.services[service]);
    const updatedServers = servers.map(s => ({
      ...s,
      services: { ...s.services, [service]: !allSelected }
    }));
    updateSelectedSiteServers(updatedServers);
  };

  const handleAddMachine = () => {
    const newMachine = {
      _rowIndex: Date.now(),
      VM: String(servers.length + 1),
      os: '',
      CPUs: 0,
      Memory: 0,
      "In Use MiB": 0,
      Powerstate: "poweredOn",
      services: { BaaS: false, DRaaS: false, IaaS: false },
      microsoftLicense: false,
      sqlLicenses: false,
      publicIp: false,
      manuallyCreated: true
    };
    updateSelectedSiteServers([...servers, newMachine]);
  };

  const osDropdownTemplate = (rowData) => {
    let options = [...commonOperatingSystems];
    if (rowData.os && !options.includes(rowData.os)) {
      options.push(rowData.os);
    }
    const dropdownOptions = options.map(os => ({ label: os, value: os }));
    return (
      <Dropdown
        value={rowData.os}
        options={dropdownOptions}
        onChange={(e) => {
          const updatedServers = servers.map(s =>
            s._rowIndex === rowData._rowIndex
              ? { ...s, os: e.value, microsoftLicense: /windows/i.test(e.value) }
              : s
          );
          updateSelectedSiteServers(updatedServers);
        }}
        placeholder="Select OS"
      />
    );
  };

  // Row class: add a green style for manually created rows.
  const rowClassName = (rowData) => {
    if (rowData.manuallyCreated) {
      return 'manually-created-row';
    } else if (/windows server 2012( r2)?/i.test(rowData.os)) {
      return 'error-row';
    } else if (/windows server 2008( r2)/i.test(rowData.os)) {
      return 'error-row';
    } else if (/windows server 2003/i.test(rowData.os)) {
      return 'error-row';
    } else if (/windows server 2000/i.test(rowData.os)) {
      return 'error-row';
    } else if (/windows server 2016/i.test(rowData.os)) {
      return 'warning-row';
    }
    return '';
  };

  const deleteRow = (rowIndex) => {
    const updatedServers = servers.filter(s => s._rowIndex !== rowIndex);
    updateSelectedSiteServers(updatedServers);
  };

  const handleFinalizeDesign = () => {
    saveServiceData();
    navigate(`/overview/${customerId}`);
  };

  // --- Sites Section Rendering ---
  const siteRowTemplate = (site) => (
    <div className="p-d-flex p-ai-center p-jc-between" style={{ borderBottom: '1px solid #ccc', padding: '0.5rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        {editingSiteId === site.id ? (
          <InputText
            value={site.name}
            onChange={(e) => {
              const updatedSites = sites.map(s =>
                s.id === site.id ? { ...s, name: e.target.value } : s
              );
              setSites(updatedSites);
            }}
            onBlur={() => {
              saveServiceData();
              setEditingSiteId(null);
            }}
            autoFocus
          />
        ) : (
          <span onClick={() => setEditingSiteId(site.id)} style={{ cursor: 'pointer' }}>
            {site.name}
          </span>
        )}
        <span style={{ marginLeft: '1rem', color: site.servers && site.servers.length > 0 ? 'green' : 'red' }}>
          {site.servers && site.servers.length > 0 ? 'Report Uploaded' : 'No Data'}
        </span>
        {(!site.servers || site.servers.length === 0) && (
          <Button
            icon="pi pi-upload"
            onClick={() => {
              setSiteToUpload(site.id);
              fileInputRef.current.click();
            }}
            className="p-button-text p-button-warning"
            tooltip="Upload RVTools"
          />
        )}
        <Button
          icon="pi pi-trash"
          onClick={() => deleteSite(site.id)}
          className="p-button-text p-button-danger"
          tooltip="Delete Site"
        />
      </div>
    </div>
  );

  // Helper: Render OS status icon with PrimeReact Tooltip for server rows.
  const osStatusIcon = (rowData) => {
    const info = getOSIconAndTooltip(rowData.os);
    if (info) {
      const color = info.icon === 'pi pi-times-circle' ? 'red' : 'orange';
      return (
        <span data-pr-tooltip={info.tooltip} data-pr-position="top">
          <i className={info.icon} style={{ color, marginRight: '0.5rem' }}></i>
        </span>
      );
    }
    return null;
  };

  // Render machine name cell with OS icon using FontAwesomeIcon.
  const machineNameBody = (rowData, options) => {
    let osIcon = null;
    if (rowData.os) {
      const lower = rowData.os.toLowerCase();
      if (lower.includes("linux")) {
        osIcon = (
          <span title="Linux" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5em' }}>
            <FontAwesomeIcon icon={faServer} style={{ fontSize: '1.5em', marginRight: '0.25em' }} />
            <FontAwesomeIcon icon={faLinux} style={{ fontSize: '1.5em' }} />
          </span>
        );
      } else if (lower.includes("windows server")) {
        osIcon = (
          <span title="Windows Server" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5em' }}>
            <FontAwesomeIcon icon={faServer} style={{ fontSize: '1.5em', marginRight: '0.25em' }} />
            <FontAwesomeIcon icon={faWindows} style={{ fontSize: '1.5em' }} />
          </span>
        );
      } else if (lower.includes("windows")) {
        osIcon = (
          <span title="Windows Desktop" style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5em' }}>
            <FontAwesomeIcon icon={faDesktop} style={{ fontSize: '1.5em', marginRight: '0.25em' }} />
            <FontAwesomeIcon icon={faWindows} style={{ fontSize: '1.5em' }} />
          </span>
        );
      }
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {osIcon}
        <span>{rowData.VM}</span>
      </div>
    );
  };

  // --- New: Handle Pre-Staged Dropdown Changes ---
  // When the user selects "Yes" for Pre-Staged Firewall or Pre-Staged DC,
  // automatically add a new machine row with a predefined name if not already present.
  const handlePreStagedChange = (type, value) => {
    if (type === 'firewall') {
      setPreStagedFirewall(value === 'Yes');
      if (value === 'Yes') {
        addPreStagedMachine("Pre-Staged FW");
      }
    } else if (type === 'dc') {
      setPreStagedDC(value === 'Yes');
      if (value === 'Yes') {
        addPreStagedMachine("Pre-Staged DC");
      }
    }
  };

  // --- Render ---
  return (
    <div className="p-4">
      <Toast ref={toast} />
      <Tooltip />
      {/* Hidden file input for site upload */}
      <input 
        type="file" 
        style={{ display: 'none' }} 
        accept=".xlsx, .xls" 
        ref={fileInputRef} 
        onChange={handleSiteFileInput} 
      />
      <h2>Service Configuration</h2>
      
      {/* Sites Section */}
      <div className="card p-4 mb-4">
        <h3>Sites</h3>
        <Button label="Add Site" icon="pi pi-plus" onClick={addSite} className="p-button-secondary" />
        <div>
          {sites.map(site => (
            <div
              key={site.id}
              onClick={() => setSelectedSiteId(site.id)}
              style={{
                cursor: 'pointer',
                backgroundColor: site.id === selectedSiteId ? '#e0e0e0' : 'transparent',
                padding: '0.5rem',
                marginTop: '0.5rem'
              }}
            >
              {siteRowTemplate(site)}
            </div>
          ))}
        </div>
      </div>
      
      {/* Global Configuration Card */}
      <div className="card p-4 mb-4">
        <h3>Global Configuration</h3>
        <div className="flex gap-4">
          <div className="grid" style={{ width: '100%' }}>
            <div className="col-8" style={{ alignContent: 'center' }}>
              <div className="grid">
                <div className="col" style={{ marginRight: '15px' }}>
                  <label style={{ marginRight: '15px' }}>SSL VPN Users</label>
                  <InputNumber
                    value={sslVpnUsers}
                    onValueChange={(e) => { setSslVpnUsers(e.value); saveServiceData(); }}
                    min={0}
                  />
                </div>
                <div className="col" style={{ marginRight: '15px' }}>
                  <label style={{ marginRight: '15px' }}>RDS CAL Licenses</label>
                  <InputNumber
                    value={rdsCalLicenses}
                    onValueChange={(e) => { setRdsCalLicenses(e.value); saveServiceData(); }}
                    min={0}
                  />
                </div>
                <div className="col" style={{ marginRight: '15px' }}>
                  <label style={{ marginRight: '15px' }}>RDS CAL Type</label>
                  <Dropdown
                    value={rdsCalType}
                    options={['Per-User', 'Per-Machine']}
                    onChange={(e) => { setRdsCalType(e.value); saveServiceData(); }}
                    placeholder="Select RDS CAL Type"
                  />
                </div>
                <div className="col" style={{ marginRight: '15px' }}>
                  <label style={{ marginRight: '15px' }}>Pre-Staged Firewall</label>
                  <Dropdown
                    value={preStagedFirewall ? 'Yes' : 'No'}
                    options={[
                      { label: 'Yes', value: 'Yes' },
                      { label: 'No', value: 'No' }
                    ]}
                    onChange={(e) => handlePreStagedChange('firewall', e.value)}
                    placeholder="Yes or No"
                  />
                </div>
                <div className="col" style={{ marginRight: '15px' }}>
                  <label style={{ marginRight: '15px' }}>Pre-Staged DC</label>
                  <Dropdown
                    value={preStagedDC ? 'Yes' : 'No'}
                    options={[
                      { label: 'Yes', value: 'Yes' },
                      { label: 'No', value: 'No' }
                    ]}
                    onChange={(e) => handlePreStagedChange('dc', e.value)}
                    placeholder="Yes or No"
                  />
                </div>
              </div>
            </div>
            <div className="col-4" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Button
                label="Finalize Design"
                onClick={handleFinalizeDesign}
                className="p-button-success mb-2"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Servers Section for Selected Site */}
      <div className="card p-4 mb-4">
        <div className="grid">
          <div className="p-col-2 mb-3 ml-3">
            <Button
              label="Add Machine"
              icon="pi pi-plus"
              onClick={handleAddMachine}
              className="p-button-secondary"
            />
          </div>
          <div className="p-col-2 mb-3 ml-6">
            Power State Filter:
            <Dropdown 
              value={powerstateFilter} 
              options={[
                { label: "All", value: "All" },
                { label: "Powered On", value: "poweredOn" },
                { label: "Powered Off", value: "poweredOff" }
              ]}
              onChange={(e) => setPowerstateFilter(e.value)}
              placeholder="Select Powerstate"
              style={{ marginLeft: '0.5rem' }}
            />
          </div>
        </div>
        <DataTable 
          value={filteredServers} 
          editMode="cell" 
          paginator 
          rows={100}
          scrollable
          scrollHeight='70vh'
          className="p-datatable-striped"
          rowClassName={rowClassName}
        >
          <Column 
            header="#" 
            body={(rowData, options) => options.rowIndex + 1} 
            style={{ width: '50px', textAlign: 'center' }}
          />
          {/* Status Column with tooltip */}
          <Column 
            header="Status" 
            body={(rowData) => (
              <div style={{ textAlign: 'center' }}>
                {osStatusIcon(rowData)}
              </div>
            )}
            style={{ width: '50px', textAlign: 'center' }}
          />
          <Column 
            field="VM" 
            header="Machine Name" 
            body={machineNameBody}
            editor={(options) => (
              <InputText 
                value={options.value} 
                onChange={(e) => options.editorCallback(e.target.value)}
              />
            )}
            onCellEditComplete={onCellEditComplete}
          />
          <Column 
            field="os" 
            header="Operating System" 
            body={osDropdownTemplate} 
          />
          <Column
            field="CPUs"
            header="CPUs"
            editor={(options) => (
              <InputNumber
                value={options.value}
                onValueChange={(ev) => options.editorCallback(ev.value)}
                min={0}
              />
            )}
            onCellEditComplete={onCellEditComplete}
          />
          <Column
            field="Memory"
            header="Memory (GB)"
            editor={(options) => (
              <InputNumber
                value={options.value}
                onValueChange={(ev) => options.editorCallback(ev.value)}
                min={0}
                step={1}
              />
            )}
            onCellEditComplete={onCellEditComplete}
          />
          <Column
            field="In Use MiB"
            header="Disk Capacity (GB)"
            editor={(options) => (
              <InputNumber
                value={options.value}
                onValueChange={(ev) => options.editorCallback(ev.value)}
                min={0}
                step={1}
              />
            )}
            onCellEditComplete={onCellEditComplete}
          />
          <Column
            header={() => (
              <div>
                <span>BaaS</span><br />
                <Checkbox 
                  checked={filteredServers.every(s => s.services.BaaS)}
                  onChange={() => selectAllService('BaaS')}
                />
              </div>
            )}
            body={(rowData) => serviceCheckboxTemplate(rowData, 'BaaS')}
          />
          <Column
            header={() => (
              <div>
                <span>DRaaS</span><br />
                <Checkbox 
                  checked={filteredServers.every(s => s.services.DRaaS)}
                  onChange={() => selectAllService('DRaaS')}
                />
              </div>
            )}
            body={(rowData) => serviceCheckboxTemplate(rowData, 'DRaaS')}
          />
          <Column
            header={() => (
              <div>
                <span>IaaS</span><br />
                <Checkbox 
                  checked={filteredServers.every(s => s.services.IaaS)}
                  onChange={() => selectAllService('IaaS')}
                />
              </div>
            )}
            body={(rowData) => serviceCheckboxTemplate(rowData, 'IaaS')}
          />
          <Column
            field="microsoftLicense"
            header="Microsoft License"
            body={(row) => (
              <Checkbox checked={row.microsoftLicense} />
            )}
          />
          <Column
            field="sqlLicenses"
            header="SQL Licenses"
            body={(row) => (
              <Checkbox
                checked={row.sqlLicenses}
                onChange={(e) =>
                  onCellEditComplete({
                    rowData: row,
                    newValue: e.checked,
                    field: 'sqlLicenses',
                  })
                }
              />
            )}
          />
          <Column
            field="publicIp"
            header="Public IP"
            body={(row) => (
              <Checkbox
                checked={row.publicIp}
                onChange={(e) =>
                  onCellEditComplete({
                    rowData: row,
                    newValue: e.checked,
                    field: 'publicIp',
                  })
                }
              />
            )}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              <Button 
                icon="pi pi-trash" 
                className="p-button-danger p-button-text" 
                onClick={() => deleteRow(rowData._rowIndex)} 
                tooltip="Delete Row" 
              />
            )}
            style={{ textAlign: 'center', width: '100px' }}
          />
        </DataTable>
      </div>
    </div>
  );
}

export default ServiceConfigPage;
